import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  Linkedin,
  PlayCircle,
} from "lucide-react";

import { ImageBox } from "@/components/site/ImageBox";
import { Testimonials } from "@/components/site/Testimonials";
import { ZoomableImage } from "@/components/site/ZoomableImage";
import { submitWaitlist } from "@/lib/waitlist.functions";
import howItWorks from "@/assets/frontier-learning-companion.webp.asset.json";
import howItWorksSm from "@/assets/frontier-sm.webp.asset.json";
import howItWorksXs from "@/assets/frontier-xs.webp.asset.json";

import howItWorksProduct from "@/assets/how-it-works-product.png.asset.json";

import atoofahAsset from "@/assets/atoofah-gierdien.webp.asset.json";
import sauceWebm from "@/assets/secret-sauce.webm.asset.json";
import sauceMp4 from "@/assets/secret-sauce.mp4.asset.json";
import saucePoster from "@/assets/secret-sauce-poster.jpg.asset.json";
import learnerPortalAsset from "@/assets/learner-a-plus-cutout.webp.asset.json";
import learnerPortalAssetSm from "@/assets/learner-a-plus-cutout-sm.webp.asset.json";
import parentPortalAsset from "@/assets/family-support-cutout.webp.asset.json";
import parentPortalAssetSm from "@/assets/family-support-cutout-sm.webp.asset.json";
import schoolPortalAsset from "@/assets/school-support-cutout.webp.asset.json";
import schoolPortalAssetSm from "@/assets/school-support-cutout-sm.webp.asset.json";





export function FeatureGridSection({ headingAs = "h2" }: { headingAs?: "h1" | "h2" }) {
  const Heading = headingAs;
  return (
    <section id="features" className="surface-warm relative pb-24 pt-32">
      <div className="mx-auto max-w-7xl px-6">
        <Heading className="font-display text-5xl leading-[0.95] text-foreground">
          This is how our learning companion helps you excel in your exams
        </Heading>
        <p className="mt-5 max-w-xl text-sm leading-relaxed text-muted-foreground">
          Your companion learns how you study, finds the gaps holding your marks back and guides you
          through the CAPS curriculum one concept at a time.
        </p>
        <div className="mt-8 w-full">
          <div className="relative min-h-[18rem] rounded-[2.5rem] bg-ink p-3 shadow-[var(--shadow-card)] sm:min-h-[22rem]">
            <div className="absolute left-1/2 top-2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-ink-foreground/30" />
            <div className="flex h-full min-h-[16rem] items-center overflow-hidden rounded-[2rem] sm:min-h-[20rem]">
              <ZoomableImage
                src={howItWorks.url}
                alt="How adaptED helps you get a bachelor pass"
              >
                <img
                  loading="lazy"
                  decoding="async"
                  src={howItWorksXs.url}
                  srcSet={`${howItWorksXs.url} 640w, ${howItWorksSm.url} 900w, ${howItWorks.url} 1672w`}
                  sizes="(max-width: 768px) 100vw, 900px"
                  alt=""
                  width={1600}
                  height={900}
                  className="w-full"
                />

              </ZoomableImage>
            </div>
          </div>
          <p className="mt-3 text-center text-xs font-medium text-muted-foreground sm:hidden">
            Tap to zoom
          </p>
        </div>
        <div className="mt-10 grid gap-10 md:grid-cols-2 md:gap-8">
          <div className="flex flex-col items-center text-center md:items-start md:text-left">
            <p className="mb-4 max-w-sm text-sm font-medium text-foreground md:max-w-xs">
              For learners & parents who want to be first in line for the adaptED learning companion.
            </p>
            <Link
              to="/join-the-pilot"
              search={{ type: "early-access" }}
              hash="contact"
              className="inline-flex rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground shadow-[var(--shadow-glow)] transition-transform hover:-translate-y-0.5"
            >
              Get early access (no school needed)
            </Link>
          </div>
          <div className="flex flex-col items-center text-center md:items-start md:text-left">
            <p className="mb-4 max-w-sm text-sm font-medium text-foreground md:max-w-xs">
              For schools ready to partner, or parents & learners nominating their school.
            </p>
            <Link
              to="/join-the-pilot"
              search={{ type: "school" }}
              className="inline-flex rounded-full bg-ink px-7 py-3.5 text-sm font-semibold text-ink-foreground shadow-[var(--shadow-card)] ring-1 ring-ink-foreground/10 transition-transform hover:-translate-y-0.5"
            >
              Nominate your school for 2027{"\u00a0"}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export function TikTokExplainerSection() {
  return (
    <section className="surface-ink relative overflow-hidden py-20">
      <div className="mx-auto max-w-3xl px-6 text-center">
        <span className="label-rule text-ink-foreground/70">Free on TikTok</span>
        <h2 className="mt-4 font-display text-4xl leading-[1.05] text-ink-foreground sm:text-5xl">
          Free Grade 10–12 explainer videos on TikTok
        </h2>
        <p className="mx-auto mt-5 max-w-2xl text-sm leading-relaxed text-ink-foreground/75">
          Understand more in just 3 minutes. We break down CAPS topics into quick, rich pockets of learning that make even the difficult stuff easier to grasp, anytime, anywhere.
        </p>
        <div className="mt-9 flex justify-center">
          <a
            href="https://www.tiktok.com/@adapted_sa?is_from_webapp=1&sender_device=pc"
            target="_blank"
            rel="noopener noreferrer"
            className="relative inline-flex items-center gap-2 rounded-full bg-primary px-8 py-4 text-sm font-semibold text-primary-foreground shadow-[var(--shadow-glow)] transition-transform hover:-translate-y-0.5"
          >
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -inset-6 -z-10 rounded-full bg-primary/25 blur-3xl"
            />
            <svg
              className="size-4 shrink-0"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
            </svg>
            Watch on TikTok
            <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
          </a>
        </div>
      </div>
    </section>
  );
}

// Ordered by search demand: CAPS > AI > personalised learning > knowledge gaps
// > safety/privacy > inclusive design > pedagogy
const differentiators = [
  {
    title: "CAPS-aligned",
    description:
      "Built on the South African curriculum, its subjects and assessment requirements. IEB, SACAI & others coming soon.",
    detail:
      "Learners always know they are preparing for the right exam, with content aligned to what actually matters for CAPS.",
  },
  {
    title: "AI-native",
    description:
      "Artificial intelligence is built into the learning experience from the ground up, not added as an afterthought.",
    detail:
      "From the first question to the final review, the companion supports learners with timely, relevant guidance.",
  },
  {
    title: "Adaptive learning engine",
    description:
      "A voice and text-based companion that adapts its explanations, questions and guidance to each learner.",
    detail:
      "Learners can talk or type and get help that feels like a patient tutor who remembers their progress.",
  },
  {
    title: "Knowledge-gap diagnostics",
    description:
      "Helps learners see where they stand, where they're stuck, and what to focus on next.",
    detail:
      "Learners get a clear picture of their strengths and the areas that need attention before exam day.",
  },
  {
    title: "Privacy-first by design",
    description:
      "Age-appropriate boundaries, privacy protections and responsible-AI safeguards are built into every interaction.",
    detail:
      "Learner data is never sold, content is filtered for age and context, and parents stay informed without compromising independence.",
  },
  {
    title: "Built for real-world access",
    description:
      "Designed to meet learners with patience, encouragement and support that respects their individual needs.",
    detail:
      "The companion meets each learner where they are, adapting to different learning styles, confidence levels and accessibility needs.",
  },
  {
    title: "Mastery-based progression",
    description:
      "Grounded in evidence-based teaching principles that support genuine understanding, not just surface recall.",
    detail:
      "The experience helps learners build durable knowledge and real comprehension, the way research shows works best.",
  },
  {
    title: "Learner profiling",
    description:
      "Designed to help learners truly understand and master what they learn, not just memorise answers for a test.",
    detail:
      "The companion keeps the focus on real understanding and steady progress, celebrating mastery over quick wins.",
  },
];


// Brand palette only: Deep Charcoal · Burnt Orange · Sand Mist accent
const cardThemes = [
  { gradient: "linear-gradient(135deg, #252525 0%, #C65D2E 100%)" },
  { gradient: "linear-gradient(135deg, #C65D2E 0%, #252525 100%)" },
  { gradient: "linear-gradient(160deg, #252525 0%, #C65D2E 100%)" },
  { gradient: "linear-gradient(160deg, #C65D2E 0%, #252525 100%)" },
  { gradient: "linear-gradient(200deg, #252525 0%, #C65D2E 100%)" },
  { gradient: "linear-gradient(200deg, #C65D2E 0%, #252525 100%)" },
];

const CARD_ACCENT = "#F4E9D4";

export function BuiltDifferentlySection() {
  const [isFlipped, setIsFlipped] = useState(false);
  const theme = cardThemes[0]!;
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    if (isFlipped) {
      video.currentTime = 0;
      void video.play().catch(() => {});
    } else {
      video.pause();
      video.currentTime = 0;
    }
  }, [isFlipped]);


  return (
    <section className="surface-warm relative overflow-x-clip pb-24 pt-12">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-4xl leading-[1.05] text-foreground sm:text-5xl">
            Every learner is different. Most learning isn't ... until now
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            adaptED combines curriculum intelligence, responsible AI and deep personalisation to create elevated learning experiences designed around South African learners.
          </p>
        </div>

        <div className="group animate-fade-in mx-auto mt-14 max-w-5xl">
          <button
            type="button"
            onClick={() => setIsFlipped((v) => !v)}
            aria-expanded={isFlipped}
            className="block w-full cursor-pointer rounded-3xl text-left outline-none [perspective:1400px] focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            <div
              className="relative grid min-h-80 w-full rounded-3xl shadow-[var(--shadow-card)] transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:-translate-y-1 group-hover:shadow-[var(--shadow-card-hover)] motion-reduce:transition-none"
              style={{
                transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
                transformStyle: "preserve-3d",
                WebkitTransformStyle: "preserve-3d",
              }}
            >

              {/* Front face */}
              <div
                className={`relative col-start-1 row-start-1 flex flex-col items-center justify-between gap-8 overflow-hidden rounded-3xl p-8 transition-[opacity,visibility] duration-300 sm:p-12 ${isFlipped ? "pointer-events-none" : ""}`}
                style={{
                  background: theme.gradient,
                  backfaceVisibility: "hidden",
                  WebkitBackfaceVisibility: "hidden",
                  transform: "translateZ(1px)",
                  opacity: isFlipped ? 0 : 1,
                  visibility: isFlipped ? "hidden" : "visible",
                  transitionDelay: "250ms",
                }}
              >

                <span className="pointer-events-none absolute -right-12 -top-12 size-48 rounded-full opacity-25 blur-3xl" style={{ background: CARD_ACCENT }} />
                <span className="pointer-events-none absolute -bottom-16 -left-16 size-64 rounded-full opacity-20 blur-3xl" style={{ background: CARD_ACCENT }} />
                <span className="pointer-events-none absolute right-8 top-8 size-3 rounded-full opacity-60" style={{ background: CARD_ACCENT }} />
                <span className="pointer-events-none absolute bottom-10 right-12 size-16 rounded-full opacity-10" style={{ background: CARD_ACCENT }} />

                <div className="relative z-10 flex flex-1 flex-col items-center justify-center text-center">
                  <h3 className="font-display text-4xl font-bold text-[#F4E9D4] sm:text-6xl">
                    Want to see our secret sauce?
                  </h3>
                  <p className="mt-4 max-w-xl text-base leading-relaxed text-[#F4E9D4]/80 sm:text-lg">
                    A glimpse of what's under the hood:
                  </p>
                  <div className="mt-6 flex flex-wrap justify-center gap-2.5">
                    {differentiators.map((item) => (
                      <span
                        key={item.title}
                        className="inline-flex items-center rounded-full bg-[#F4E9D4]/15 px-4 py-2 text-sm font-semibold text-[#F4E9D4] sm:text-base"
                      >
                        {item.title}
                      </span>
                    ))}
                  </div>
                </div>

                <span className="relative z-10 mx-auto inline-flex min-h-11 items-center gap-1.5 rounded-full bg-[#F4E9D4]/15 px-4 py-2.5 text-sm font-semibold text-[#F4E9D4] transition-all duration-300 group-hover:bg-[#F4E9D4]/25">
                  Flip to reveal
                  <ChevronDown className="size-4 rotate-[-90deg]" aria-hidden="true" />
                </span>
              </div>

              {/* Back face */}
              <div
                className={`relative col-start-1 row-start-1 flex flex-col items-center justify-between gap-8 overflow-hidden rounded-3xl p-8 transition-[opacity,visibility] duration-300 sm:p-12 ${isFlipped ? "" : "pointer-events-none"}`}
                style={{
                  background: theme.gradient,
                  backfaceVisibility: "hidden",
                  WebkitBackfaceVisibility: "hidden",
                  transform: "rotateY(180deg) translateZ(1px)",
                  opacity: isFlipped ? 1 : 0,
                  visibility: isFlipped ? "visible" : "hidden",
                  transitionDelay: "250ms",
                }}
              >

                <span className="pointer-events-none absolute -left-12 -top-12 size-48 rounded-full opacity-25 blur-3xl" style={{ background: CARD_ACCENT }} />
                <span className="pointer-events-none absolute -bottom-16 -right-16 size-64 rounded-full opacity-20 blur-3xl" style={{ background: CARD_ACCENT }} />
                <span className="pointer-events-none absolute left-8 top-8 size-3 rounded-full opacity-60" style={{ background: CARD_ACCENT }} />

                <div className="relative z-10 flex flex-1 flex-col items-center justify-center text-center">
                  <h3 className="font-display text-4xl font-bold text-[#F4E9D4] sm:text-6xl">
                    Hahaha, you thought we'd just give it away?
                  </h3>
                  <p className="mt-4 max-w-xl text-base leading-relaxed text-[#F4E9D4]/80 sm:text-lg">
                    The real magic only unlocks once you're in. Some things are better experienced than explained.
                  </p>
                  <video
                    ref={videoRef}
                    muted
                    loop
                    playsInline
                    preload="none"
                    poster={saucePoster.url}
                    aria-hidden="true"
                    className="pointer-events-none mt-6 size-48 rounded-2xl object-cover shadow-[var(--shadow-card)] sm:size-56"
                  >
                    <source src={sauceWebm.url} type="video/webm" />
                    <source src={sauceMp4.url} type="video/mp4" />
                  </video>
                  <Link
                    to="/join-the-pilot"
                    className="mt-8 inline-flex min-h-11 items-center gap-2 rounded-full bg-[#F4E9D4] px-6 py-3 text-base font-bold text-[#252525] transition-all duration-300 hover:bg-white"
                  >
                    Join the pilot to find out
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </Link>
                </div>

                <span className="relative z-10 mx-auto inline-flex min-h-11 items-center gap-1.5 rounded-full bg-[#F4E9D4]/15 px-4 py-2.5 text-sm font-semibold text-[#F4E9D4] transition-all duration-300 group-hover:bg-[#F4E9D4]/25">
                  Flip back
                  <ChevronDown className="size-4 rotate-90" aria-hidden="true" />
                </span>
              </div>
            </div>
          </button>
        </div>
      </div>
    </section>
  );
}

export function HowItWorksSection() {
  return (
    <section id="how" className="relative bg-background/90 py-24">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-2">
        <div className="relative">
          <div className="overflow-hidden rounded-3xl bg-card shadow-[var(--shadow-card)]">
            <img
                  loading="lazy"
                  decoding="async"
              src={howItWorksProduct.url}
              alt="How the adaptED learning companion guides a learner through the CAPS curriculum"
              className="w-full"
            />
          </div>
          <div className="absolute -bottom-8 right-4 w-40 rounded-2xl border border-border bg-card p-5 text-center shadow-[var(--shadow-card)] sm:w-48">
            <PlayCircle className="mx-auto size-9 stroke-1 text-foreground" />
            <p className="mt-2 text-xs font-medium text-muted-foreground">Product walkthrough only available to early-access waitlist</p>
          </div>
        </div>

        <div>
          <span className="label-rule text-muted-foreground">HOW IT WORKS</span>
          <h2 className="mt-4 font-display text-4xl leading-[1.05] text-foreground sm:text-5xl">
            Meet the AI learning companion
            <br />
            that learns how you learn
          </h2>
          <p className="mt-5 max-w-md text-sm leading-relaxed text-muted-foreground">
            adaptED discovers where you’re struggling, explains difficult concepts in a way that
            clicks, and adapts every session around what you need next. No more guessing what to
            study. Just focused, personalised support that helps you understand more, close your
            gaps and walk into every exam better prepared.
          </p>
          <Link
            to="/join-the-pilot"
            search={{ type: "early-access" }}
            hash="contact"
            className="mt-9 inline-flex rounded-full bg-ink px-7 py-3.5 text-sm font-semibold text-ink-foreground transition-transform hover:-translate-y-0.5"
          >
            Join the early-access waitlist
          </Link>
        </div>
      </div>
    </section>
  );
}

export function ProductReleasesSection() {
  return (
    <section className="surface-warm relative py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mx-auto max-w-3xl text-center">
          <span className="label-rule text-muted-foreground">Latest releases</span>
          <h2 className="mt-4 font-display text-4xl leading-[1.05] text-foreground sm:text-5xl">
            What's new at adaptED
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            Explore our newest tools built to help South African learners prepare, practise and perform.
          </p>
        </div>

        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          <article className="group relative overflow-hidden rounded-3xl bg-card p-8 shadow-[var(--shadow-card)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[var(--shadow-card-hover)] sm:p-10">
            <span
              className="pointer-events-none absolute -right-10 -top-10 size-40 rounded-full opacity-20 blur-3xl"
              style={{ background: "#C65D2E" }}
            />
            <div className="relative z-10 flex h-full flex-col items-center text-center lg:items-start lg:text-left">
              <span className="w-fit rounded-full bg-muted px-3 py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                In development
              </span>
              <h3 className="mt-6 font-display text-3xl font-bold leading-tight text-foreground sm:text-4xl">
                AI learning companion
              </h3>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-muted-foreground">
                A safe, learning-first companion that adapts to how you learn, explains difficult concepts & closes your knowledge gaps.
              </p>
              <span className="mt-6 inline-flex w-fit items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary-strong">
                Coming soon
              </span>
              <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-3 pt-2">
                <Link
                  to="/join-the-pilot"
                  search={{ type: "early-access" }}
                  hash="contact"
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-[var(--shadow-glow)] transition-transform hover:-translate-y-0.5"
                >
                  Get early access — no school needed
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/join-the-pilot"
                  search={{ type: "school" }}
                  hash="contact"
                  className="inline-flex items-center gap-1.5 rounded-full border border-primary px-5 py-2.5 text-sm font-semibold text-primary-strong transition-colors hover:bg-primary/10"
                >
                  Nominate your school for 2027
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </article>

          <article className="group relative overflow-hidden rounded-3xl bg-card p-8 shadow-[var(--shadow-card)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[var(--shadow-card-hover)] sm:p-10">
            <span
              className="pointer-events-none absolute -left-10 -top-10 size-40 rounded-full opacity-20 blur-3xl"
              style={{ background: "#252525" }}
            />
            <div className="relative z-10 flex h-full flex-col items-center text-center lg:items-start lg:text-left">
              <span className="w-fit rounded-full bg-muted px-3 py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Live product
              </span>
              <h3 className="mt-6 font-display text-3xl font-bold leading-tight text-foreground sm:text-4xl">
                Introducing Quizzy
              </h3>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-muted-foreground">
                An intelligent quiz engine that finds your knowledge gaps before the exam does, so you know exactly what to focus on next.
              </p>
              <span className="mt-6 inline-flex w-fit items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary-strong">
                Available now
              </span>
              <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-3 pt-2">
                <a
                  href="https://www.quizzyonline.co.za"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-full border border-primary px-5 py-2.5 text-sm font-semibold text-primary-strong transition-colors hover:bg-primary/10"
                >
                  Try Quizzy free
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                </a>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}

const portalPreviews = [
  {
    name: "Learners",
    description: "Personalised learning paths, knowledge-gap analysis and safe AI support that adapts to how you study.",
    image: learnerPortalAsset.url,
    imageSm: learnerPortalAssetSm.url,
    alt: "Learner using adaptED to achieve academic success",
  },
  {
    name: "Parents",
    description: "Clear progress overviews, gap alerts and actionable tips so you can support your child with confidence.",
    image: parentPortalAsset.url,
    imageSm: parentPortalAssetSm.url,
    alt: "Family supporting a learner with adaptED",
  },
  {
    name: "Schools",
    description: "Class-wide insights, targeted intervention signals and dashboards that help teachers help learners.",
    image: schoolPortalAsset.url,
    imageSm: schoolPortalAssetSm.url,
    alt: "School community supporting a learner with adaptED",
  },
];

export function ConnectedPortalsPreview() {
  return (
    <section className="surface-warm relative py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mx-auto max-w-3xl text-center">
          <span className="label-rule text-muted-foreground">Connected Portals</span>
          <h2 className="mt-4 font-display text-4xl leading-[1.05] text-foreground sm:text-5xl">
            One learner. One support system. Three connected portals.
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            adaptED links learners, parents and schools through a shared view of progress, gaps and the support needed next.
          </p>
        </div>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {portalPreviews.map((portal) => (
            <article
              key={portal.name}
              className="group flex flex-col overflow-hidden rounded-3xl bg-card shadow-[var(--shadow-card)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[var(--shadow-card-hover)]"
            >
              <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted/50">
                <img
                  loading="lazy"
                  decoding="async"
                  src={portal.image}
                  srcSet={`${portal.imageSm} 480w, ${portal.image} 760w`}
                  sizes="(max-width: 640px) 90vw, (max-width: 1024px) 45vw, 380px"
                  alt={portal.alt}
                  className="h-full w-full object-contain p-4 transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-1 flex-col p-6 sm:p-8">
                <h3 className="font-display text-2xl font-bold text-foreground">{portal.name}</h3>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-muted-foreground">{portal.description}</p>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-12 flex justify-center">
          <Link
            to="/connected-portals"
            className="inline-flex items-center gap-2 rounded-full bg-ink px-8 py-4 text-sm font-semibold text-ink-foreground transition-transform hover:-translate-y-0.5"
          >
            Explore the connected portals
            <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export function TeamSection() {
  const [openBio, setOpenBio] = useState<number | null>(null);

  const members = [
    {
      name: "Atoofah Gierdien",
      role: "Chief Executive Officer",
      image: atoofahAsset.url,
      linkedin: "https://www.linkedin.com/in/atoofah-gierdien",
      bio: "Atoofah Gierdien is a multidisciplinary founder and product leader who builds at the intersection of human behaviour, technology and learning. She combines creativity and strategic thinking with expertise in research, product design, behavioural analytics and rapid prototyping. Her professional experience as a project manager and data analyst enables her to turn complex problems into structured, evidence-led products.\n\nMore than a decade of tutoring has given her a deep understanding of South African learners. She holds a Master's degree in Information Systems and graduated Summa Cum Laude with a double major in Information Systems and Industrial Psychology.\n\nAn Allan Gray Fellow and published researcher, Atoofah brings the creative vision, product judgement, technical fluency and learner insight required to build adaptED.",
    },
  ];

  return (
    <section id="team" className="surface-ink relative py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="text-center">
          <h2 className="font-display text-4xl text-ink-foreground sm:text-5xl">Founder-led, mission-driven.</h2>
          <p className="mx-auto mt-4 max-w-xl text-sm text-ink-foreground/75">
            adaptED combines deep classroom insight, research discipline, and responsible AI — with a founder-led team united by one focus: building technology that serves South African learners first.
          </p>
        </div>

        <div className="mx-auto mt-14 grid max-w-md gap-6 justify-items-center">
          {members.map((member, i) => (
            <article
              key={i}
              className="group flex flex-col rounded-2xl border border-ink-foreground/10 bg-ink-foreground/5 p-3 text-left backdrop-blur transition-colors hover:border-primary/40"
            >
              {member.image ? (
                <div className="aspect-[4/5] w-full overflow-hidden rounded-xl">
                  <img
                  loading="lazy"
                  decoding="async"
                    src={member.image}
                    alt={`Portrait of ${member.name}`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
              ) : (
                <ImageBox
                  rounded="rounded-xl"
                  className="aspect-[4/5] w-full overflow-hidden"
                />
              )}
              <div className="px-2 pt-5">
                <h3 className="font-display text-base font-bold text-ink-foreground">{member.name}</h3>
                <p className="mt-1 text-xs text-ink-foreground/75">{member.role}</p>
              </div>
              {member.bio && openBio === i && (
                <div id={`team-bio-${i}`} className="px-2 pt-4">
                  <p className="text-sm leading-relaxed text-ink-foreground/80 whitespace-pre-line">
                    {member.bio}
                  </p>
                </div>
              )}
              <div className="mt-auto flex items-center justify-between border-t border-ink-foreground/10 px-2 pt-4">
                <a
                  href={member.linkedin}
                  target="_top"
                  rel="noopener noreferrer"
                  aria-label={`${member.name} LinkedIn profile`}
                  className="inline-flex size-10 items-center justify-center rounded-md bg-linkedin text-linkedin-foreground transition-transform hover:scale-105"
                >
                  <Linkedin className="size-4" />
                </a>
                {member.bio && (
                  <button
                    type="button"
                    aria-expanded={openBio === i}
                    aria-controls={`team-bio-${i}`}
                    onClick={() => setOpenBio(openBio === i ? null : i)}
                    className="inline-flex min-h-11 items-center gap-2 text-xs font-semibold text-ink-foreground/80 transition-colors hover:text-primary-on-ink"
                  >
                    <span className="inline-flex size-5 items-center justify-center rounded-full border border-primary/60 text-primary-on-ink">
                      {openBio === i ? (
                        <ChevronDown className="size-3 rotate-180" />
                      ) : (
                        <ArrowRight className="size-3" />
                      )}
                    </span>
                    {openBio === i ? "Close" : "Read her Bio"}
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export function TeamSupportSection() {
  return (
    <section className="surface-warm relative py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid gap-16 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7">
            <h2 className="font-display text-4xl leading-[1.05] text-foreground sm:text-5xl">
              Supported by Industry Experience
            </h2>
            <div className="mt-6 space-y-5 text-lg leading-relaxed text-muted-foreground">
              <p>
                Behind our founder is an advisory group of experienced leaders across education, psychometrics, human performance, artificial intelligence, technology, research and entrepreneurship.
              </p>
              <p>
                Our advisors provide strategic guidance, challenge our assumptions and help us navigate the academic, technical and operational decisions involved in building learning technology for young people.
              </p>
              <p>
                They help us build with greater rigour, accountability and long-term perspective.
              </p>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-3xl border border-border/60 bg-card/70 p-8 shadow-[var(--shadow-card)] backdrop-blur-sm sm:p-10">
              <h3 className="font-display text-2xl text-foreground sm:text-3xl">
                Built With Educators, Not Around Them
              </h3>
              <div className="mt-5 space-y-4 text-base leading-relaxed text-muted-foreground">
                <p>
                  We believe the strongest learning technology is shaped by the people closest to the learner.
                </p>
                <p>
                  Our approach is to work alongside teachers, curriculum and subject specialists, school leaders, parents and learners throughout the development of adaptED. Their experience helps us test our assumptions, strengthen our content and ensure that our technology responds to real educational needs.
                </p>
                <p className="font-medium text-foreground">
                  adaptED is designed to support educators and strengthen their ability to help every learner. It is not designed to replace them.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16">
          <div className="relative overflow-hidden rounded-3xl bg-ink px-8 py-14 text-ink-foreground sm:px-12 sm:py-16 lg:px-16">
            <div className="relative z-10 max-w-2xl">
              <h2 className="font-display text-4xl leading-[1.05] sm:text-5xl">
                Partner With Us
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-ink-foreground/80">
                Help shape the future of personalised learning in South Africa.
              </p>
              <p className="mt-3 text-lg leading-relaxed text-ink-foreground/80">
                We are inviting schools to participate in private pilots, provide structured feedback and help us build technology that works in real classrooms.
              </p>
              <Link
                to="/join-the-pilot"
                hash="contact"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground shadow-[var(--shadow-glow)] transition-transform hover:-translate-y-0.5"
              >
                Register Your School&apos;s Interest
                <ArrowRight className="size-4" />
              </Link>
            </div>
            <div className="pointer-events-none absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-primary/20 blur-3xl" />
            <div className="pointer-events-none absolute -right-10 top-0 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />
          </div>
        </div>
      </div>
    </section>
  );
}


export function TestimonialsSection() {
  return (
    <section id="stories" className="surface-ink relative py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="text-center">
          <h2 className="font-display text-4xl text-ink-foreground sm:text-5xl">Testimonials</h2>
          <p className="mx-auto mt-4 max-w-xl text-sm text-ink-foreground/75">Real feedback from learners and educators&nbsp;</p>
        </div>
        <div className="mt-14">
          <Testimonials />
        </div>
      </div>
    </section>
  );
}

type Kind = "school" | "nomination" | "individual";

const GRADE_LIST = ["10", "11", "12", "Other"] as const;

const PROVINCE_LIST = [
  "Eastern Cape",
  "Free State",
  "Gauteng",
  "KwaZulu-Natal",
  "Limpopo",
  "Mpumalanga",
  "Northern Cape",
  "North West",
  "Western Cape",
] as const;

const CHALLENGE_LIST = [
  "Improving pass rates",
  "Closing learning gaps",
  "Exam readiness",
  "Learner engagement",
  "Other",
] as const;

const fieldClass =
  "w-full rounded-lg border border-border bg-background px-4 py-3 text-sm text-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20";
const labelClass = "mb-1.5 block text-xs font-semibold text-foreground";

export function PilotFormSection({
  heading = "Join the Pilot",
  defaultType = "general",
  headingAs = "h2",
}: {
  heading?: string;
  defaultType?: "learner" | "parent" | "school" | "general" | "early-access";
  headingAs?: "h1" | "h2";
}) {
  const submit = useServerFn(submitWaitlist);
  const [startedAt] = useState(() => Date.now());
  const [honeypot, setHoneypot] = useState("");
  const [kind, setKind] = useState<Kind>(() => {
    if (defaultType === "early-access") return "individual";
    if (defaultType === "learner" || defaultType === "parent") return "nomination";
    return "school";
  });
  const [form, setForm] = useState({
    // shared
    fullName: "",
    email: "",
    phone: "",
    message: "",
    schoolName: "",
    // school application
    schoolLocation: "",
    matricLearnerCount: "",
    biggestChallenge: "" as "" | (typeof CHALLENGE_LIST)[number],
    biggestChallengeOther: "",
    position: "",
    // nomination
    role: (defaultType === "parent" ? "parent" : "learner") as "learner" | "parent",
    province: "" as "" | (typeof PROVINCE_LIST)[number],
    // individual early access
    grade: "" as "" | (typeof GRADE_LIST)[number],
  });
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const canSubmit =
    kind === "individual"
      ? consent &&
        form.fullName.trim() !== "" &&
        emailValid &&
        form.grade !== "" &&
        form.province !== ""
      : kind === "school"
      ? consent &&
        form.schoolName.trim() !== "" &&
        form.schoolLocation.trim() !== "" &&
        form.matricLearnerCount.trim() !== "" &&
        form.biggestChallenge !== "" &&
        (form.biggestChallenge !== "Other" || form.biggestChallengeOther.trim() !== "") &&
        form.fullName.trim() !== "" &&
        form.position.trim() !== "" &&
        emailValid &&
        form.phone.trim() !== ""
      : form.fullName.trim() !== "" &&
        emailValid &&
        form.schoolName.trim() !== "" &&
        form.province !== "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === "submitting" || !canSubmit) return;
    setStatus("submitting");
    setErrorMessage("");
    try {
      const payload =
        kind === "individual"
          ? {
              kind: "individual" as const,
              role: form.role,
              fullName: form.fullName,
              email: form.email,
              grade: form.grade as (typeof GRADE_LIST)[number],
              province: form.province as (typeof PROVINCE_LIST)[number],
              phone: form.phone,
              message: form.message,
              consent: true as const,
              website: honeypot,
              startedAt,
            }
          : kind === "school"
          ? {
              kind: "school" as const,
              schoolName: form.schoolName,
              schoolLocation: form.schoolLocation,
              matricLearnerCount: form.matricLearnerCount,
              biggestChallenge: form.biggestChallenge as (typeof CHALLENGE_LIST)[number],
              biggestChallengeOther: form.biggestChallengeOther,
              fullName: form.fullName,
              position: form.position,
              email: form.email,
              phone: form.phone,
              message: form.message,
              consent: true as const,
              website: honeypot,
              startedAt,
            }
          : {
              kind: "nomination" as const,
              role: form.role,
              fullName: form.fullName,
              email: form.email,
              schoolName: form.schoolName,
              province: form.province as (typeof PROVINCE_LIST)[number],
              phone: form.phone,
              message: form.message,
              website: honeypot,
              startedAt,
            };
      await submit({ data: payload });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMessage(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    }
  };

  const tabClass = (active: boolean) =>
    `flex-1 rounded-lg px-4 py-3 text-sm font-semibold transition-colors ${
      active
        ? "bg-primary text-primary-foreground"
        : "bg-background text-foreground hover:bg-muted"
    }`;

  return (
    <section id="contact" className="relative bg-primary py-24 text-primary-foreground">
      <div className="mx-auto max-w-3xl px-6 text-center">
        <div className="mb-6 flex justify-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/30 bg-primary-foreground/10 px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-foreground/20"
          >
            <ArrowLeft className="size-4" aria-hidden="true" />
            Back to home
          </Link>
        </div>
        {headingAs === "h1" ? (
          <h1 className="font-display text-4xl sm:text-5xl">{heading}</h1>
        ) : (
          <h2 className="font-display text-4xl sm:text-5xl">{heading}</h2>
        )}
        <p className="mx-auto mt-4 max-w-xl text-sm text-primary-foreground">
          The adaptED pilot programme commences in 2027. Schools apply for the Founding School
          Programme, and learners or parents can nominate their school so we can reach out. You do
          not need a school to get started: join individual early access and we will let you know as
          soon as the learning companion opens up to you.
        </p>

        {status === "success" ? (
          <div className="mx-auto mt-10 max-w-xl rounded-2xl bg-card p-8 text-center shadow-[var(--shadow-card)]">
            <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-primary/20">
              <svg className="size-7 text-primary-strong" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="mt-5 font-display text-2xl text-foreground">
              {kind === "school"
                ? "Application received"
                : kind === "individual"
                  ? "You're on the early-access list"
                  : "Thank you for nominating your school"}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {kind === "school"
                ? "Our team will review your school's details and contact you about the Founding School Programme."
                : kind === "individual"
                  ? "We will email you as soon as early access to the learning companion opens. In the meantime, you can start finding your knowledge gaps with Quizzy."
                  : "We will contact the school you nominated about joining the 2027 pilot programme."}
            </p>
            {kind === "individual" && (
              <a
                href="https://www.quizzyonline.co.za"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex rounded-lg bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground"
              >
                Try Quizzy now
              </a>
            )}
          </div>
        ) : (
          <form
            className="mx-auto mt-10 max-w-xl rounded-2xl bg-card p-6 text-left shadow-[var(--shadow-card)] sm:p-8"
            onSubmit={handleSubmit}
          >
            <fieldset>
              <legend className={labelClass}>I am signing up as</legend>
              <div className="flex flex-col gap-2 rounded-xl border border-border p-2 sm:flex-row">
                <button type="button" onClick={() => setKind("individual")} className={tabClass(kind === "individual")} aria-pressed={kind === "individual"}>
                  Early access (no school needed)
                </button>
                <button type="button" onClick={() => setKind("school")} className={tabClass(kind === "school")} aria-pressed={kind === "school"}>
                  A school
                </button>
                <button type="button" onClick={() => setKind("nomination")} className={tabClass(kind === "nomination")} aria-pressed={kind === "nomination"}>
                  Nominate a school
                </button>
              </div>
            </fieldset>

            {kind === "individual" ? (
              <>
                <p className="mt-6 text-xs leading-relaxed text-muted-foreground">
                  No school needed. Tell us a little about yourself and we will notify you as soon as
                  early access to the adaptED learning companion is available to you.
                </p>

                <label htmlFor="ea-role" className={`${labelClass} mt-6`}>I am a</label>
                <select id="ea-role" name="role" value={form.role} onChange={handleChange} className={fieldClass}>
                  <option value="learner">Learner</option>
                  <option value="parent">Parent / guardian</option>
                </select>

                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="ea-name" className={labelClass}>Your name</label>
                    <input id="ea-name" name="fullName" value={form.fullName} onChange={handleChange} required maxLength={100} autoComplete="name" className={fieldClass} />
                  </div>
                  <div>
                    <label htmlFor="ea-email" className={labelClass}>Email address</label>
                    <input id="ea-email" type="email" name="email" value={form.email} onChange={handleChange} required maxLength={255} autoComplete="email" className={fieldClass} />
                  </div>
                  <div>
                    <label htmlFor="ea-grade" className={labelClass}>
                      {form.role === "parent" ? "Your child's grade" : "Your grade"}
                    </label>
                    <select id="ea-grade" name="grade" value={form.grade} onChange={handleChange} required className={fieldClass}>
                      <option value="">Select a grade</option>
                      {GRADE_LIST.map((g) => (
                        <option key={g} value={g}>{g === "Other" ? "Other" : `Grade ${g}`}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="ea-province" className={labelClass}>Province</label>
                    <select id="ea-province" name="province" value={form.province} onChange={handleChange} required className={fieldClass}>
                      <option value="">Select a province</option>
                      {PROVINCE_LIST.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <label htmlFor="ea-phone" className={`${labelClass} mt-4`}>
                  Contact number <span className="font-normal text-muted-foreground">(optional)</span>
                </label>
                <input id="ea-phone" type="tel" name="phone" value={form.phone} onChange={handleChange} maxLength={50} autoComplete="tel" className={fieldClass} />

                <div className="mt-6 rounded-xl border-l-4 border-primary bg-muted/60 p-4">
                  <label className="flex items-start gap-2 text-xs text-foreground">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      required
                      className="mt-0.5 size-4 shrink-0 accent-[var(--color-primary)]"
                    />
                    <span>
                      I consent to adaptED contacting me about early access and related updates. My
                      information will be processed in accordance with POPIA and will not be shared
                      with third parties without my consent, unless required by law.
                    </span>
                  </label>
                </div>
              </>
            ) : kind === "school" ? (
              <>
                <div className="mt-6 rounded-xl border-l-4 border-primary bg-muted/60 p-4">
                  <p className="font-display text-sm font-bold text-foreground">Consent</p>
                  <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                    By submitting this form, you confirm that the information provided is accurate to
                    the best of your knowledge. You consent to adaptED contacting you regarding the
                    Founding School Programme, including verifying your school&apos;s eligibility and
                    sharing information about the pilot programme. Your information will be processed
                    in accordance with the Protection of Personal Information Act (POPIA) and will not
                    be shared with third parties without your consent, unless required by law.
                  </p>
                  <label className="mt-3 flex items-start gap-2 text-xs text-foreground">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      required
                      className="mt-0.5 size-4 shrink-0 accent-[var(--color-primary)]"
                    />
                    <span>I have read and agree to the above.</span>
                  </label>
                </div>

                <p className="mt-8 font-display text-sm font-bold text-foreground">
                  Tell us about your school
                </p>

                <label htmlFor="pilot-schoolName" className={`${labelClass} mt-4`}>School name</label>
                <input id="pilot-schoolName" name="schoolName" value={form.schoolName} onChange={handleChange} required maxLength={200} className={fieldClass} />

                <label htmlFor="pilot-schoolLocation" className={`${labelClass} mt-4`}>School location (town/city &amp; province)</label>
                <input id="pilot-schoolLocation" name="schoolLocation" value={form.schoolLocation} onChange={handleChange} required maxLength={200} className={fieldClass} />

                <label htmlFor="pilot-matric" className={`${labelClass} mt-4`}>
                  Approximately how many Grade 12 (Matric) learners does your school have?
                </label>
                <input id="pilot-matric" name="matricLearnerCount" value={form.matricLearnerCount} onChange={handleChange} required maxLength={50} className={fieldClass} />

                <fieldset className="mt-6">
                  <legend className={labelClass}>
                    What is the biggest academic challenge your school is currently facing?
                  </legend>
                  <div className="space-y-2">
                    {CHALLENGE_LIST.map((option) => (
                      <label key={option} className="flex items-center gap-2 text-sm text-foreground">
                        <input
                          type="radio"
                          name="biggestChallenge"
                          value={option}
                          checked={form.biggestChallenge === option}
                          onChange={handleChange}
                          required
                          className="size-4 accent-[var(--color-primary)]"
                        />
                        <span>{option === "Other" ? "Other:" : option}</span>
                      </label>
                    ))}
                  </div>
                  {form.biggestChallenge === "Other" && (
                    <input
                      name="biggestChallengeOther"
                      value={form.biggestChallengeOther}
                      onChange={handleChange}
                      placeholder="Tell us more"
                      maxLength={300}
                      className={`mt-3 ${fieldClass}`}
                    />
                  )}
                </fieldset>

                <p className="mt-8 font-display text-sm font-bold text-foreground">How do we contact you?</p>

                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="pilot-name" className={labelClass}>Your name</label>
                    <input id="pilot-name" name="fullName" value={form.fullName} onChange={handleChange} required maxLength={100} autoComplete="name" className={fieldClass} />
                  </div>
                  <div>
                    <label htmlFor="pilot-position" className={labelClass}>Your position</label>
                    <input id="pilot-position" name="position" value={form.position} onChange={handleChange} required maxLength={150} className={fieldClass} />
                  </div>
                  <div>
                    <label htmlFor="pilot-email" className={labelClass}>Email address</label>
                    <input id="pilot-email" type="email" name="email" value={form.email} onChange={handleChange} required maxLength={255} autoComplete="email" className={fieldClass} />
                  </div>
                  <div>
                    <label htmlFor="pilot-phone" className={labelClass}>Contact number</label>
                    <input id="pilot-phone" type="tel" name="phone" value={form.phone} onChange={handleChange} required maxLength={50} autoComplete="tel" className={fieldClass} />
                  </div>
                </div>
              </>
            ) : (
              <>
                <p className="mt-6 text-xs leading-relaxed text-muted-foreground">
                  Only schools can sign up for the 2027 pilot. Nominate your school below and our team
                  will contact them about joining the Founding School Programme.
                </p>

                <label htmlFor="pilot-role" className={`${labelClass} mt-6`}>I am a</label>
                <select id="pilot-role" name="role" value={form.role} onChange={handleChange} className={fieldClass}>
                  <option value="learner">Learner</option>
                  <option value="parent">Parent / guardian</option>
                </select>

                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="pilot-nom-name" className={labelClass}>Your name</label>
                    <input id="pilot-nom-name" name="fullName" value={form.fullName} onChange={handleChange} required maxLength={100} autoComplete="name" className={fieldClass} />
                  </div>
                  <div>
                    <label htmlFor="pilot-nom-email" className={labelClass}>Email address</label>
                    <input id="pilot-nom-email" type="email" name="email" value={form.email} onChange={handleChange} required maxLength={255} autoComplete="email" className={fieldClass} />
                  </div>
                </div>

                <label htmlFor="pilot-nom-school" className={`${labelClass} mt-4`}>School name</label>
                <input id="pilot-nom-school" name="schoolName" value={form.schoolName} onChange={handleChange} required maxLength={200} className={fieldClass} />

                <label htmlFor="pilot-province" className={`${labelClass} mt-4`}>Province</label>
                <select id="pilot-province" name="province" value={form.province} onChange={handleChange} required className={fieldClass}>
                  <option value="">Select a province</option>
                  {PROVINCE_LIST.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>

                <label htmlFor="pilot-nom-phone" className={`${labelClass} mt-4`}>
                  Contact number <span className="font-normal text-muted-foreground">(optional)</span>
                </label>
                <input id="pilot-nom-phone" type="tel" name="phone" value={form.phone} onChange={handleChange} maxLength={50} autoComplete="tel" className={fieldClass} />
              </>
            )}

            <label htmlFor="pilot-message" className={`${labelClass} mt-4`}>
              {kind === "individual" ? "Subjects or goals you want help with" : "Message"}{" "}
              <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <textarea id="pilot-message" name="message" value={form.message} onChange={handleChange} rows={4} maxLength={1000} className={`resize-none ${fieldClass}`} />

            {/* Honeypot: hidden from real users, catches bots that fill every field */}
            <input
              type="text"
              name="website"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              hidden
              className="pointer-events-none absolute left-[-9999px] size-0 opacity-0"
            />

            {status === "error" && (
              <p role="alert" className="mt-4 text-sm text-destructive">{errorMessage}</p>
            )}

            <button
              type="submit"
              disabled={status === "submitting" || !canSubmit}
              className="mt-6 w-full rounded-lg bg-primary py-3.5 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5 disabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {status === "submitting"
                ? "Submitting..."
                : kind === "individual"
                  ? "Get early access"
                  : kind === "school"
                    ? "Submit school application"
                    : "Nominate my school"}
            </button>
          </form>
        )}

        <p className="mt-6 text-xs text-primary-foreground">
          We respect your privacy and will only use your details to share relevant updates about adaptED.
        </p>
      </div>
    </section>
  );
}
